import { TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import { appStateActionTypes, appStateSelectors } from "../../modules/appState";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { timelineViewSettingsActionTypes } from "../../modules/definition/timelineViewSettings";
import { itemSelectors } from "../../modules/items";
import { timelineRangeSelectors } from "../../modules/timelineRange";
import preprocessorQuery from "../../utils/preprocessorQuery";
import {
  getFlashPlaceholder,
  getIconPlaceholder,
  getSwitchViewInstruction,
  getTipIdGenerator,
  getViewNameLabel
} from "../utils";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "TIMELINE_CUSTOMIZATION";
const GROUP_LABEL = "Timeline Customization";
const GROUP_DESCRIPTION = "Learn how to customize the visual appearance and behaviour of your timeline.";
const GROUP_GATE = state => itemSelectors.getAllEventIdsDisplayedOnTimeline(state).size >= 10;
const GROUP_GATE_MESSAGE = "You must have at least ten events in your timeline to begin this tutorial.";
const GROUP_APPLICABLE_GATE = null;
const GROUP_APPLICABLE_GATE_MESSAGE = null;
const GROUP_COMPLETED_MESSAGE = "Great work! Use what you have learned to customize how your timeline is displayed.";
const GROUP_LINKS = [
  { label: "Learn more about customizing Timeline View", path: "https://help.timeline.app/article/144-timeline-view" }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("CUSTOMIZE_ITEM_TYPES_TIMELINE"),
      label: "Show people as their own event bars in the timeline",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `In the view footer, click the ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_CARD_DETAILS,
            "show types icon"
          )} ${getIconPlaceholder("footerTimelineTypes")}`,
          isNumbered: true
        },
        { label: "Toggle the item types that you want to draw as events on the timeline.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      canSkip: true
    },
    {
      id: idGenerator("CUSTOMIZE_ITEM_DETAILS_TIMELINE"),
      label: "Customize what item details are shown",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `In the view footer, click the ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_CARD_DETAILS,
            "item card icon"
          )} ${getIconPlaceholder("footerCardDetails")}`,
          isNumbered: true
        },
        { label: "Tick the item details that you want shown for each event on the timeline.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        (action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IDENTIFIER ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DATES ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DURATION ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_NARRATIVE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKS ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKED_BY ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IMAGE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_SUMMARY ||
          action.type === timelineViewSettingsActionTypes.TOGGLE_SHOW_RELATIONSHIP_TYPES)
    },
    {
      id: idGenerator("SET_TIMELINE_LAYOUT"),
      label: "Customize how the timeline arranges events",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `In the view footer, click the ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_TIMELINE_LAYOUT,
            "layout icon"
          )} ${getIconPlaceholder("footerTimelineLayoutExpanded")}`,
          isNumbered: true
        },
        { label: "Choose a different layout to set how the timeline should arrange events.", isNumbered: true },
        {
          label: "Tip: The differences between the layouts will be more noticeable on timelines with more events.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === timelineViewSettingsActionTypes.CHANGE_LAYOUT
    },
    {
      id: idGenerator("SET_TIMELINE_RANGE"),
      label: "Limit the timeline to a date range",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `In the view footer, click the ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_TIMELINE_RANGE,
            "timeline range"
          )} (e.g. Infinite Scroll)`,
          isNumbered: true
        },
        { label: "Specify start and end dates to restrict timeline scrolling within those dates", isNumbered: true },
        {
          label:
            "Tip: If you have events that fall outside of your set range, the timeline will extend to show them but the range selector will show a warning indicator.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => {
        const rangeFrom = timelineRangeSelectors.getRangeFrom(state);
        const rangeTo = timelineRangeSelectors.getRangeTo(state);
        return isDateValid(rangeFrom) || isDateValid(rangeTo);
      }
    },
    {
      id: idGenerator("TIMELINE_SYNC_SCROLL"),
      label: "See two timelines that scroll independently of each other",
      steps: [
        { label: `Open ${getViewNameLabel(TIMELINE_VIEW)} in two split views, one above the other`, isNumbered: true },
        {
          label: `In the view footer, click on the ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_LOCK_SCROLLING,
            "scroll lock icon"
          )} ${getIconPlaceholder("footerScrollLock")} to deactivate it.`,
          isNumbered: true
        },
        { label: "The two timelines can now be scrolled independently.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === appStateActionTypes.UNLOCK_SCROLLING
    }
  ];
} else if (isTablet) {
  tips = [
    {
      id: idGenerator("ADJUST_TIMELINE_ZOON"),
      label: "Use a pinch gesture on Timeline View to adjust zoom",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Place two fingers on the Timeline View and use a pinch gesture to expand or contract the view`,
          isNumbered: true
        },
        { label: `The time scale will zoom in or out around your gesture position`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      canSkip: true
    },
    {
      id: idGenerator("CUSTOMIZE_ITEM_TYPES_TIMELINE"),
      label: "Show people as their own event bars in the timeline",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.IOS_VIEW_DETAILS,
            "view settings icon"
          )} ${getIconPlaceholder("footerCardDetails")} in the Sidebar`,
          isNumbered: true
        },
        {
          label: `Under "Show types on timeline", toggle the additional item types you want to draw as events on the timeline`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      canSkip: true
    },
    {
      id: idGenerator("CUSTOMIZE_ITEM_DETAILS_TIMELINE"),
      label: "Customize what item details are shown",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.IOS_VIEW_DETAILS,
            "view settings icon"
          )} ${getIconPlaceholder("footerCardDetails")} in the Sidebar`,
          isNumbered: true
        },
        {
          label: `In the View Settings panel, use the switches under "Item Display" to turn on or off additional item details`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        (action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IDENTIFIER ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DATES ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DURATION ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_NARRATIVE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKS ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKED_BY ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IMAGE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_SUMMARY ||
          action.type === timelineViewSettingsActionTypes.TOGGLE_SHOW_RELATIONSHIP_TYPES)
    },
    {
      id: idGenerator("SET_TIMELINE_LAYOUT"),
      label: "Customize how the timeline arranges events",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.IOS_VIEW_DETAILS,
            "view settings icon"
          )} ${getIconPlaceholder("footerCardDetails")} in the Sidebar`,
          isNumbered: true
        },
        {
          label: `Tap on "Timeline layout" and choose a different layout to set how the timeline should arrange events.`,
          isNumbered: true
        },
        {
          label: "Tip: The differences between the layouts will be more noticeable on timelines with more events.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === timelineViewSettingsActionTypes.CHANGE_LAYOUT
    },
    {
      id: idGenerator("TIMELINE_SYNC_SCROLL"),
      label: "See two timelines that scroll independently of each other",
      steps: [
        { label: `Open ${getViewNameLabel(TIMELINE_VIEW)} in two split views, one above the other`, isNumbered: true },
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.IOS_VIEW_DETAILS,
            "view settings icon"
          )} ${getIconPlaceholder("footerCardDetails")} in the Sidebar`,
          isNumbered: true
        },
        { label: `Toggle off the "Sync Scrolling" option`, isNumbered: true },
        { label: "The two timelines can now be scrolled independently.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === appStateActionTypes.UNLOCK_SCROLLING
    }
  ];
} else if (isMobile) {
  tips = [
    {
      id: idGenerator("ADJUST_TIMELINE_ZOON"),
      label: "Use a pinch gesture on Timeline View to adjust zoom",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Place two fingers on the Timeline View and use a pinch gesture to expand or contract the view`,
          isNumbered: true
        },
        { label: `The time scale will zoom in or out around your gesture position`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      canSkip: true
    },
    {
      id: idGenerator("CUSTOMIZE_ITEM_TYPES_TIMELINE"),
      label: "Show people as their own event bars in the timeline",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the more icon ${getIconPlaceholder(
            "ellipsis"
          )} in the toolbar and then View settings ${getIconPlaceholder("footerCardDetails")}`,
          isNumbered: true
        },
        {
          label: `Under "Show types on timeline", toggle the additional item types you want to draw as events on the timeline`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      canSkip: true
    },
    {
      id: idGenerator("CUSTOMIZE_ITEM_DETAILS_TIMELINE"),
      label: "Customize what item details are shown",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the more icon ${getIconPlaceholder(
            "ellipsis"
          )} in the toolbar and then View settings ${getIconPlaceholder("footerCardDetails")}`,
          isNumbered: true
        },
        {
          label: `In the View Settings panel, use the switches under "Item Display" to turn on or off additional item details`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        (action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IDENTIFIER ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DATES ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DURATION ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_NARRATIVE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKS ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKED_BY ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IMAGE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_SUMMARY ||
          action.type === timelineViewSettingsActionTypes.TOGGLE_SHOW_RELATIONSHIP_TYPES)
    },
    {
      id: idGenerator("SET_TIMELINE_LAYOUT"),
      label: "Customize how the timeline arranges events",
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the more icon ${getIconPlaceholder(
            "ellipsis"
          )} in the toolbar and then View settings ${getIconPlaceholder("footerCardDetails")}`,
          isNumbered: true
        },
        {
          label: `Tap on "Timeline layout" and choose a different layout to set how the timeline should arrange events.`,
          isNumbered: true
        },
        {
          label: "Tip: The differences between the layouts will be more noticeable on timelines with more events.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === timelineViewSettingsActionTypes.CHANGE_LAYOUT
    }
  ];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
